import type { BootstrapData, Translation } from './types';
import type { AppNotification } from './types';
import { formatMessage } from './i18n-format';

export type NotificationCopy = Pick<Translation, 'friendRequestFrom' | 'clubMember' | 'handicapShort'>;

type NotificationFeedBase = {
  title: string;
  body: string;
  icon: string;
  createdAt?: string;
  read: boolean;
};

export type NotificationFeedItem =
  | (NotificationFeedBase & {
      kind: 'friendRequest';
      id: string;
      requestId: string;
    })
  | (NotificationFeedBase & {
      kind: 'note';
      id: string;
      linkType?: string | null;
      linkId?: string | null;
    });

export function getNotificationIcon(linkType?: string | null, title = '') {
  const normalized = title.toLowerCase();
  if (linkType === 'friendRequest') return '👤';
  if (linkType === 'matchplay') return '⛳';
  if (linkType === 'matchResult') return '🏆';
  if (linkType === 'matchCancelled') return '✕';
  if (normalized.includes('friend')) return '👤';
  if (normalized.includes('duel')) return '⚔️';
  if (normalized.includes('tee-time') || normalized.includes('booking')) return '📅';
  if (normalized.includes('join')) return '🙋';
  return '🔔';
}

function feedTimestamp(item: NotificationFeedItem) {
  if (!item.createdAt) return 0;
  const value = Date.parse(item.createdAt);
  return Number.isNaN(value) ? 0 : value;
}

function isUnreadNotification(note: AppNotification) {
  return !note.readAt;
}

function appendNotificationItems(
  items: NotificationFeedItem[],
  linkedRequestIds: Set<string>,
  notifications: AppNotification[],
) {
  for (const note of notifications) {
    if (note.linkType === 'friendRequest' && note.linkId) {
      linkedRequestIds.add(note.linkId);
      items.push({
        kind: 'friendRequest',
        id: note.id,
        requestId: note.linkId,
        title: note.title,
        body: note.body,
        icon: getNotificationIcon(note.linkType, note.title),
        createdAt: note.createdAt,
        read: !isUnreadNotification(note),
      });
      continue;
    }

    if (note.title.toLowerCase().includes('friend request')) {
      continue;
    }

    items.push({
      kind: 'note',
      id: note.id,
      title: note.title,
      body: note.body,
      linkType: note.linkType,
      linkId: note.linkId,
      icon: getNotificationIcon(note.linkType, note.title),
      createdAt: note.createdAt,
      read: !isUnreadNotification(note),
    });
  }
}

function appendPendingFriendRequests(
  items: NotificationFeedItem[],
  linkedRequestIds: Set<string>,
  friendRequests: BootstrapData['friendRequests'],
  copy: NotificationCopy,
) {
  for (const request of friendRequests ?? []) {
    if (linkedRequestIds.has(request.id)) continue;
    items.push({
      kind: 'friendRequest',
      id: `request-${request.id}`,
      requestId: request.id,
      title: formatMessage(copy.friendRequestFrom, { name: request.user.name }),
      body: `${request.user.homeClub ?? copy.clubMember} · ${copy.handicapShort} ${request.user.handicap}`,
      icon: '👤',
      read: false,
    });
  }
}

export function buildNotificationFeed(data: BootstrapData, copy: NotificationCopy): NotificationFeedItem[] {
  const items: NotificationFeedItem[] = [];
  const linkedRequestIds = new Set<string>();
  appendNotificationItems(items, linkedRequestIds, data.notifications ?? []);
  appendPendingFriendRequests(items, linkedRequestIds, data.friendRequests, copy);
  return items.sort((left, right) => feedTimestamp(right) - feedTimestamp(left));
}

export function buildNotificationHistoryFeed(
  notifications: AppNotification[],
  friendRequests: BootstrapData['friendRequests'],
  copy: NotificationCopy,
): NotificationFeedItem[] {
  const items: NotificationFeedItem[] = [];
  const linkedRequestIds = new Set<string>();
  appendNotificationItems(items, linkedRequestIds, notifications);
  appendPendingFriendRequests(items, linkedRequestIds, friendRequests, copy);
  return items.sort((left, right) => feedTimestamp(right) - feedTimestamp(left));
}

export function getNotificationCount(data: BootstrapData, copy: NotificationCopy) {
  return buildNotificationFeed(data, copy).length;
}

export function getHomeNotificationPreview(data: BootstrapData, copy: NotificationCopy, limit = 3) {
  return buildNotificationFeed(data, copy).slice(0, limit);
}

export function getUnreadNotificationCount(feed: NotificationFeedItem[]) {
  return feed.filter(item => !item.read).length;
}

export function getNotificationIconStyle(linkType?: string | null, title = '') {
  const normalized = title.toLowerCase();
  if (linkType === 'friendRequest') return 'friend';
  if (linkType === 'matchplay') return 'matchplay';
  if (linkType === 'matchResult') return 'result';
  if (linkType === 'matchCancelled') return 'cancelled';
  if (normalized.includes('friend')) return 'friend';
  if (normalized.includes('duel')) return 'duel';
  if (normalized.includes('tee-time') || normalized.includes('booking')) return 'booking';
  if (normalized.includes('join')) return 'join';
  return 'default';
}

export type NotificationPressHandlers = {
  onOpenActiveMatch: () => void;
  onOpenMatchHistory: (matchId: string) => void;
  onMatchCancelled: () => void;
  setScreen: (screen: import('./types').Screen) => void;
};

export function handleNotificationPress(item: NotificationFeedItem, handlers: NotificationPressHandlers) {
  if (item.kind === 'friendRequest') {
    handlers.setScreen('friends');
    return;
  }
  if (item.linkType === 'matchplay') {
    handlers.onOpenActiveMatch();
    return;
  }
  if (item.linkType === 'matchCancelled') {
    handlers.onMatchCancelled();
    return;
  }
  if (item.linkType === 'matchResult' && item.linkId) {
    handlers.onOpenMatchHistory(item.linkId);
    return;
  }
  handlers.setScreen('friends');
}

export async function markNotificationItemRead(
  item: NotificationFeedItem,
  request: (path: string) => Promise<unknown>,
) {
  if (item.read) {
    return;
  }
  if (item.kind === 'friendRequest') {
    await request(`/friends/${item.requestId}/view`);
    return;
  }
  await request(`/notifications/${item.id}/read`);
}
